// useUserInitialization.ts
import { useEffect, useRef } from 'react';
import { App as AntdApp } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from './useAuth';
import { useUserStore } from '../stores/useUserStore';
import { getAndSanitize } from '../utils/sanitization';

/**
 * Hook to handle one-time user initialization and periodic status monitoring
 * This runs AFTER successful login and ensures user data is loaded and valid
 */
export const useUserInitialization = () => {
  const navigate = useNavigate();
  const logout = useAuthStore(state => state.logout);
  const { message: contextMessage } = AntdApp.useApp();
  const isInitializedRef = useRef(false);
  const lastCheckRef = useRef<number>(0);
  const isCheckingRef = useRef(false);

  // One-time initialization when app starts with valid token
  useEffect(() => {
    const initializeUser = async () => {
      // Prevent duplicate initialization
      if (isInitializedRef.current || isCheckingRef.current) {
        return;
      }

      const token = getAndSanitize('token');
      const userId = getAndSanitize('userId');

      // Only initialize if we have both token and userId
      if (!token || !userId) {
        return;
      }

      isCheckingRef.current = true;

      try {

        // Fetch user data with force refresh to ensure fresh data
        const user = await useUserStore.getState().fetchUser(userId, true);

        if (!user) {
          console.error('[UserInit] Failed to fetch user data');
          contextMessage.error('Error al cargar datos de usuario. Por favor, inicia sesión nuevamente.');
          logout();
          navigate('/');
          return;
        }

        if (!user.active) {
          contextMessage.error('Tu cuenta ha sido desactivada. Contacta al administrador.');
          logout();
          navigate('/');
          return;
        }


        isInitializedRef.current = true;
        lastCheckRef.current = Date.now();
      } catch (error) {
        console.error('[UserInit] Error during user initialization:', error);

        // Don't force logout on initialization errors - the axios interceptor will handle 401s
        // This prevents logout loops on temporary network issues
        if ((error as any)?.response?.status === 401) {
        }
      } finally {
        isCheckingRef.current = false;
      }
    };

    initializeUser();
  }, []); // Run only once on mount

  // Periodic user status check (every 5 minutes)
  useEffect(() => {
    const FIVE_MINUTES = 5 * 60 * 1000;

    const checkUserStatus = async () => {
      const token = getAndSanitize('token');
      const userId = getAndSanitize('userId');

      if (!token || !userId || !isInitializedRef.current) {
        return; // Not initialized yet or logged out
      }

      const now = Date.now();
      if (now - lastCheckRef.current < FIVE_MINUTES) {
        return; // Too soon, skip check
      }

      if (isCheckingRef.current) {
        return;
      }

      isCheckingRef.current = true;
      lastCheckRef.current = now;

      try {

        const user = await useUserStore.getState().fetchUser(userId, true);

        if (!user || !user.active) {
          contextMessage.warning('Tu sesión ha expirado o tu cuenta fue desactivada.');
          logout();
          navigate('/');
        }
      } catch (error) {
        console.error('[UserInit] Error during periodic status check:', error);
        // Axios interceptor will handle 401 errors globally
      } finally {
        isCheckingRef.current = false;
      }
    };

    // Check every minute (actual check happens every 5 minutes due to lastCheckRef)
    const interval = setInterval(checkUserStatus, 60 * 1000);

    return () => clearInterval(interval);
  }, [navigate, logout, contextMessage]);

  return {
    isInitialized: isInitializedRef.current
  };
};
